import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Loader2, ArrowUpRight, ArrowDownLeft, Percent, AlertTriangle,
  CheckCircle2, FileText, Upload, X, FileSpreadsheet, Info, ExternalLink, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/formatUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const formatCurrency = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a || 0);

// Tiny info-tooltip helper — pure UX sugar to move long explanation paragraphs
// out of the form body. Stays accessible (keyboard focusable trigger).
const InfoHint = ({ text }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" tabIndex={0} className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs whitespace-pre-wrap">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const creditCategories = ['bank_transfer', 'upi', 'cheque', 'cash', 'credit_card', 'other'];
// Debit categories are now sourced dynamically from /api/expense-categories
// (managed under Settings → Expense Categories). The fallback list below
// only kicks in if the API request is in-flight or fails.
const FALLBACK_DEBIT_CATEGORIES = ['infrastructure', 'software', 'travel', 'meals', 'office', 'marketing', 'salary', 'other'];

const emptyForm = {
  type: 'debit', client_name: '', amount: '', category: 'other', date: '',
  reference: '', description: '', paid_by: '',
  project_id: '', project_name: '', invoice_id: '', invoice_number: '', invoice_subtotal: 0,
  invoice_total: 0, invoice_type: '', invoice_status: '',
  bank_account: '', bank_txn_amount: '', tds_amount: '', invoice_file: '', invoice_file_original_name: '',
};

/**
 * Reusable Add/Edit Transaction Dialog.
 *
 * Props:
 *  - open (bool): controls visibility
 *  - onClose (fn): called when the user closes/cancels
 *  - initialValues (object | null): partial form values to pre-fill on create
 *      (e.g. { type: 'credit', project_id, project_name, client_name,
 *              invoice_id, invoice_number, invoice_subtotal, bank_account })
 *      Amount is intentionally left blank to support partial payments.
 *  - editTxn (object | null): full transaction object → puts dialog in Edit mode
 *  - onSaved (fn): called after successful create/update (parent should refresh)
 */
export const TransactionFormDialog = ({ open, onClose, initialValues = null, editTxn = null, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Project search
  const [projectSearch, setProjectSearch] = useState('');
  const [projectResults, setProjectResults] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectRef = useRef(null);

  // Invoices for selected project
  const [projectInvoices, setProjectInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Bank accounts
  const [bankAccounts, setBankAccounts] = useState([]);
  const [debitCategories, setDebitCategories] = useState(FALLBACK_DEBIT_CATEGORIES);

  // Invoice file upload (for debit)
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const [tdsOption, setTdsOption] = useState('none');
  // Tracks the last `amount` / `bank_txn_amount` values the dialog auto-filled
  // on a TDS-radio click — so a subsequent toggle (2% → 10%, or → No TDS) can
  // safely recompute ONLY if the user hasn't manually edited the value since.
  const [tdsAutoFill, setTdsAutoFill] = useState({ amount: null, bankTxn: null });
  // Manual override for invoice status after credit transaction save.
  // 'paid' | 'unpaid' — only relevant for credit + tax invoice in create mode.
  const [markPaidChoice, setMarkPaidChoice] = useState('paid');

  // Load bank accounts once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/transactions/bank-accounts');
        if (!cancelled && res.data.success) setBankAccounts(res.data.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Load the dynamic debit category list once on mount. Falls back to the
  // hardcoded constant if the API is unreachable so the form is still usable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/expense-categories');
        if (!cancelled && res.data?.success && Array.isArray(res.data.data) && res.data.data.length) {
          setDebitCategories(res.data.data.map(c => c.slug));
        }
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadProjectInvoices = useCallback(async (projectId) => {
    if (!projectId) { setProjectInvoices([]); return; }
    setLoadingInvoices(true);
    try {
      const res = await api.get(`/transactions/project-invoices/${projectId}`);
      if (res.data.success) setProjectInvoices(res.data.data);
    } catch { setProjectInvoices([]); }
    finally { setLoadingInvoices(false); }
  }, []);

  // Capture a stable snapshot of initialValues/editTxn the moment the dialog
  // opens, so subsequent parent re-renders or async bank-account loads don't
  // wipe user input.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // open edge: false → true. Initialise once.
      wasOpenRef.current = true;

      if (editTxn) {
        const f = {
          type: editTxn.type,
          client_name: editTxn.client_name || '',
          amount: editTxn.amount?.toString() || '',
          category: editTxn.category || 'other',
          date: editTxn.date || '',
          reference: editTxn.reference || '',
          description: editTxn.description || '',
          paid_by: editTxn.paid_by || '',
          project_id: editTxn.project_id || '',
          project_name: editTxn.project_name || '',
          invoice_id: editTxn.invoice_id || '',
          invoice_number: editTxn.invoice_number || '',
          invoice_subtotal: editTxn.invoice_subtotal || 0,
          bank_account: editTxn.bank_account || '',
          bank_txn_amount: editTxn.bank_txn_amount?.toString() || '',
          tds_amount: editTxn.tds_amount?.toString() || '',
          invoice_file: editTxn.invoice_file || '',
          invoice_file_original_name: editTxn.invoice_file_original_name || '',
        };
        setForm(f);
        setEditId(editTxn.id);
        setProjectSearch(editTxn.project_name && editTxn.client_name
          ? `${editTxn.project_name} (${editTxn.client_name})`
          : (editTxn.project_name || ''));
        // Prefer the original (vendor-issued) filename when available so
        // finance can recognise the file by the name they uploaded.
        setUploadedFileName(editTxn.invoice_file_original_name || editTxn.invoice_file || '');
        setTdsOption('none');
        setTdsAutoFill({ amount: null, bankTxn: null });
        setMarkPaidChoice('paid');
        if (editTxn.project_id) loadProjectInvoices(editTxn.project_id);
      } else {
        const initial = initialValues || {};
        const todayIso = new Date().toISOString().slice(0, 10);   // yyyy-mm-dd
        const primaryBankId = bankAccounts.find(a => a.is_primary)?.id
          || bankAccounts[0]?.id
          || '';
        const merged = {
          ...emptyForm,
          type: initial.type || 'credit',
          category: (initial.type || 'credit') === 'credit' ? 'bank_transfer' : 'other',
          // Default the bank account to the primary one — overridden later
          // by handleSelectInvoice if the invoice has its own bank.
          bank_account: primaryBankId,
          // Default date to today so the user doesn't have to type it for
          // the common "just got paid, log it now" flow.
          date: todayIso,
          ...initial,
          // Always leave amount blank on create (supports partial payments)
          amount: '',
        };
        // `initialValues.date` (if passed) overrides today's date; otherwise
        // ensure todayIso wins even when initial has an empty string for date.
        if (!merged.date) merged.date = todayIso;
        setForm(merged);
        setEditId(null);
        setProjectSearch(initial.project_name && initial.client_name
          ? `${initial.project_name} (${initial.client_name})`
          : (initial.project_name || ''));
        setUploadedFileName('');
        setTdsOption('none');
        setTdsAutoFill({ amount: null, bankTxn: null });
        // Default Tax Invoice Status radio to "Mark as Paid" — the common
        // case is that the user is recording a payment that has actually
        // landed in the bank, so the auto-generated tax invoice should also
        // be marked paid (and trigger the tax-invoice email).
        setMarkPaidChoice('paid');
        if (initial.project_id) loadProjectInvoices(initial.project_id);
        else setProjectInvoices([]);
      }
    } else if (!open && wasOpenRef.current) {
      // close edge: reset for next open
      wasOpenRef.current = false;
    }
  }, [open, editTxn, initialValues, loadProjectInvoices]);

  // When editing a migrated transaction, the loaded form has invoice_id but
  // not invoice_subtotal (the legacy doc only stores the FK). Once the
  // project's invoices have loaded, hydrate invoice_subtotal/_total/_type
  // from the matching row AND pre-select the correct TDS radio so the
  // % badge matches reality.
  useEffect(() => {
    if (!form.invoice_id || !projectInvoices.length) return;
    const inv = projectInvoices.find(i => i.id === form.invoice_id);
    if (!inv) return;
    setForm(f => {
      // Hydrate any of the dependent fields that are still empty. This is
      // critical when the dialog opens with `initialValues.invoice_id`
      // pre-set (e.g. via the "+ Add Transaction" kebab on the Invoices
      // page) — without this the user lands on an Add Transaction modal
      // with an invoice pre-selected but no description / subtotal /
      // bank_account auto-filled.
      const next = { ...f };
      if (!f.invoice_subtotal && !f.invoice_total) {
        next.invoice_subtotal = inv.subtotal || inv.total || 0;
        next.invoice_total = inv.total || 0;
        next.invoice_type = inv.invoice_type || f.invoice_type || '';
        next.invoice_status = inv.status || f.invoice_status || '';
      }
      // Pre-fill the Description from the invoice's description when the
      // user hasn't typed one yet. (Mandatory-field UX guideline.)
      if ((!f.description || !f.description.trim()) && inv.description) {
        next.description = inv.description;
      }
      // Bank account from the invoice if it was set there.
      if (!f.bank_account && inv.bank_account_id) {
        next.bank_account = inv.bank_account_id;
      }
      // Avoid a no-op state update — return previous if nothing changed.
      const changed =
        next.invoice_subtotal !== f.invoice_subtotal ||
        next.invoice_total !== f.invoice_total ||
        next.invoice_type !== f.invoice_type ||
        next.invoice_status !== f.invoice_status ||
        next.description !== f.description ||
        next.bank_account !== f.bank_account;
      return changed ? next : f;
    });
    // Pre-select the matching radio based on the stored TDS % vs the
    // computed % from invoice subtotal. Use a small tolerance so 2.0 ≈ 2.
    const tds = parseFloat(form.tds_amount);
    const base = parseFloat(inv.subtotal || inv.total || 0);
    if (tds > 0 && base > 0) {
      const pct = (tds / base) * 100;
      if (Math.abs(pct - 2) < 0.05) setTdsOption('2');
      else if (Math.abs(pct - 10) < 0.05) setTdsOption('10');
      else setTdsOption('none');
    } else {
      setTdsOption('none');
    }
  }, [form.invoice_id, projectInvoices, form.tds_amount]);

  // Apply primary bank account default once accounts load,
  // but only if no bank is selected yet (don't clobber user input).
  // Fallback chain: primary bank → first available bank → keep empty.
  useEffect(() => {
    if (!open || bankAccounts.length === 0) return;
    setForm(f => {
      if (f.bank_account) return f;
      const primary = bankAccounts.find(a => a.is_primary)?.id
        || bankAccounts[0]?.id
        || '';
      return primary ? { ...f, bank_account: primary } : f;
    });
  }, [open, bankAccounts]);

  // Smart-suggest Mark-as-Paid when the entered credit amount covers the
  // invoice total. Only upgrades the choice to 'paid' — never downgrades to
  // 'unpaid' — so the user's "Mark as Paid" default (or their manual
  // selection) is preserved as the amount evolves.
  useEffect(() => {
    if (editId) return;
    if (form.type !== 'credit') return;
    if (form.invoice_type !== 'tax' && form.invoice_type !== 'proforma') return;
    if (!form.invoice_total) return;
    const amt = parseFloat(form.amount);
    if (!amt) return;
    if (amt >= form.invoice_total) setMarkPaidChoice('paid');
  }, [form.amount, form.invoice_total, form.invoice_type, form.type, editId]);

  // Project search debounce
  useEffect(() => {
    if (!projectSearch || projectSearch.length < 2) { setProjectResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/transactions/search-projects?q=${encodeURIComponent(projectSearch)}`);
        if (res.data.success) { setProjectResults(res.data.data); setShowProjectDropdown(true); }
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [projectSearch]);

  // Click outside to close project dropdown
  useEffect(() => {
    const handle = (e) => {
      if (projectRef.current && !projectRef.current.contains(e.target)) setShowProjectDropdown(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleSelectProject = async (project) => {
    setForm(f => ({
      ...f,
      project_id: project.id,
      project_name: project.name,
      client_name: project.client_name,
      invoice_id: '',
      invoice_number: '',
    }));
    setProjectSearch(project.client_name ? `${project.name} (${project.client_name})` : project.name);
    setShowProjectDropdown(false);
    loadProjectInvoices(project.id);
  };

  const handleSelectInvoice = (inv) => {
    setForm(f => ({
      ...f,
      invoice_id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_subtotal: inv.subtotal || inv.total || 0,
      invoice_total: inv.total || 0,
      invoice_type: inv.invoice_type || '',
      invoice_status: inv.status || '',
      bank_account: inv.bank_account_id || f.bank_account,
      // Pre-fill the transaction description with the invoice's description
      // — but only when the user hasn't already typed one. The Description
      // field is now mandatory, so giving them a sensible default removes
      // a friction point for the common case.
      description: (f.description && f.description.trim()) ? f.description : (inv.description || ''),
    }));
    // Default to "Mark as Paid" on invoice select — recording a payment is
    // by far the common case. Don't downgrade an already-paid choice.
    setMarkPaidChoice('paid');
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/transactions/upload-invoice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setForm(f => ({
          ...f,
          invoice_file: res.data.data.path,
          // Persist the original filename so it's available next time the
          // transaction is opened — even on a different device.
          invoice_file_original_name: res.data.data.filename || file.name,
        }));
        setUploadedFileName(file.name);
        toast.success('Invoice uploaded');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingInvoice(false); }
  };

  const getInvoiceBaseAmount = () => {
    // TDS rate is ALWAYS computed against the linked invoice's pre-tax
    // subtotal. If no invoice is picked, return 0 so radios become a no-op
    // (prevents the old fallback of `amount / 1.18` which incorrectly
    // derived the base from the transaction amount).
    const subtotal = parseFloat(form.invoice_subtotal);
    return subtotal > 0 ? subtotal : 0;
  };

  const calcTdsPercent = () => {
    const tds = parseFloat(form.tds_amount);
    const base = getInvoiceBaseAmount();
    if (!tds || !base || base <= 0) return null;
    return ((tds / base) * 100).toFixed(1);
  };

  const handleTdsOptionChange = (option) => {
    const base = getInvoiceBaseAmount();
    // Guard: switching radios when no invoice is selected would compute
    // TDS off `amount` and write a wrong number. Require an invoice first.
    if (option !== 'none' && base <= 0) {
      toast.error('Select an invoice first — TDS % is calculated on its pre-tax amount.');
      return;
    }
    setTdsOption(option);
    if (option === 'none') {
      setForm(f => ({ ...f, tds_amount: '' }));
      // Per spec: clicking "No TDS" should NOT clear the amount the user
      // might want to keep. We simply stop auto-managing the values.
      setTdsAutoFill({ amount: null, bankTxn: null });
      return;
    }
    const pct = parseFloat(option);
    const tdsAmt = Math.round(base * pct / 100);
    setForm(f => {
      const next = { ...f, tds_amount: tdsAmt > 0 ? tdsAmt.toString() : '' };
      // Auto-fill the received amount + bank statement amount the user is
      // about to type — but only when (a) we're not editing an existing
      // txn, (b) the invoice has a real `total`, and (c) the user hasn't
      // already typed something different. We track the last value we
      // auto-filled so a 2% → 10% toggle can safely re-update it.
      if (editId) return next;
      const invTotal = parseFloat(f.invoice_total) || 0;
      if (invTotal <= 0) return next;
      const expected = Math.round(invTotal - tdsAmt);   // 29500 - 500 = 29000
      const cur = (f.amount || '').toString().trim();
      const prevAuto = tdsAutoFill.amount;
      // Update `amount` only if it's empty OR still equals the previous
      // auto-filled value (i.e. user hasn't manually overridden it).
      if (cur === '' || (prevAuto !== null && cur === String(prevAuto))) {
        next.amount = String(expected);
      }
      // Same rule for the bank-statement amount.
      const curBank = (f.bank_txn_amount || '').toString().trim();
      const prevAutoBank = tdsAutoFill.bankTxn;
      if (curBank === '' || (prevAutoBank !== null && curBank === String(prevAutoBank))) {
        next.bank_txn_amount = String(expected);
      }
      // Remember what we just wrote so the next toggle can recheck.
      setTdsAutoFill({ amount: expected, bankTxn: expected });
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.amount || !form.date) { toast.error('Amount and date required'); return; }
    if (!form.description || !form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (form.type === 'credit' && form.project_id && !form.invoice_id) {
      toast.error('Please select an invoice for the chosen project. TDS is calculated on the invoice pre-tax amount.');
      return;
    }
    if (form.type === 'debit' && !form.bank_account) { toast.error('Bank account is required for debit transactions'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        bank_txn_amount: form.bank_txn_amount ? parseFloat(form.bank_txn_amount) : null,
        tds_amount: form.tds_amount ? parseFloat(form.tds_amount) : null,
      };
      let res;
      if (editId) {
        res = await api.put(`/transactions/${editId}`, payload);
        toast.success('Transaction updated');
      } else {
        res = await api.post('/transactions', payload);
        toast.success(`${form.type === 'credit' ? 'Credit' : 'Debit'} transaction created`);
        if (res.data?.auto_tax_invoice && !res.data.auto_tax_invoice.already_exists) {
          toast.success(`Tax Invoice ${res.data.auto_tax_invoice.invoice_number} auto-generated from proforma`, { duration: 5000 });
        }
      }

      // Apply manual Paid/Unpaid override on the relevant tax invoice
      // (in create mode only). For tax invoices it's the linked invoice itself;
      // for proforma it's the auto-generated tax invoice (newly created or
      // pre-existing if the proforma was already converted).
      if (!editId && form.type === 'credit' && form.invoice_id) {
        let targetInvoiceId = null;
        let targetLabel = form.invoice_number;
        if (form.invoice_type === 'tax') {
          targetInvoiceId = form.invoice_id;
        } else if (form.invoice_type === 'proforma') {
          const auto = res?.data?.auto_tax_invoice;
          if (auto?.invoice_id) {
            targetInvoiceId = auto.invoice_id;
            targetLabel = auto.invoice_number || targetLabel;
          }
        }
        if (targetInvoiceId) {
          try {
            if (markPaidChoice === 'paid') {
              await api.post(`/invoices/${targetInvoiceId}/mark-paid`);
              toast.success(`Invoice ${targetLabel} marked as paid`);
              // After marking paid, the backend tries to send the tax-invoice
              // email but the PDF must already be persisted. We trigger a
              // server-side render then retry the email send. Fire-and-forget
              // so the user isn't blocked on SMTP latency.
              (async () => {
                try {
                  await api.post(`/invoices/${targetInvoiceId}/regenerate-pdf`);
                  const sendRes = await api.post(`/invoices/${targetInvoiceId}/send-tax-invoice`);
                  // The endpoint returns 200 even when the email is skipped
                  // (no recipients, missing PDF, etc). Surface the actual
                  // status so the user isn't told "sent" when nothing went
                  // out — common pitfall on legacy clients that don't have
                  // billing_emails configured.
                  const status = sendRes.data?.data?.status || 'sent';
                  if (status === 'sent') {
                    toast.success(`Tax-invoice email queued for ${targetLabel}`);
                  } else if (status === 'skipped_no_recipients') {
                    toast.warning(
                      `Tax invoice NOT emailed — no billing email on this client. Add one on the Clients page.`,
                      { duration: 7000 }
                    );
                  } else if (status === 'skipped_no_pdf' || status === 'skipped_pdf_stale') {
                    toast.warning(`Tax invoice NOT emailed — PDF not ready (${status.replace(/_/g, ' ')}). Retry from the invoice row.`);
                  } else if (status === 'failed') {
                    toast.error(`Tax-invoice email send failed: ${sendRes.data?.data?.error || 'unknown error'}`);
                  } else {
                    toast.warning(`Tax-invoice email status: ${status.replace(/_/g, ' ')}`);
                  }
                } catch (e) {
                  // Soft failure — backend already recorded `skipped_no_pdf`
                  // so the user can manually retry from the invoice row.
                  console.warn('Auto PDF+email failed:', e?.response?.data || e);
                }
              })();
            } else if (markPaidChoice === 'unpaid') {
              await api.post(`/invoices/${targetInvoiceId}/mark-unpaid`);
            }
          } catch (err) {
            toast.error(`Status update failed: ${err.response?.data?.detail || 'Unknown error'}`);
          }
        }
      }

      onSaved?.(res?.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const switchType = (newType) => {
    const primaryBank = bankAccounts.find(a => a.is_primary)?.id
      || bankAccounts[0]?.id
      || '';
    const todayIso = new Date().toISOString().slice(0, 10);
    setForm(prev => ({
      ...emptyForm,
      type: newType,
      category: newType === 'credit' ? 'bank_transfer' : 'other',
      // Preserve the user's existing selections across a type switch — the
      // primary-bank + today fallbacks only kick in if nothing was set yet.
      // (bankAccounts can still be loading on first open; the dedicated
      // useEffect below picks up the primary as soon as accounts arrive.)
      bank_account: prev.bank_account || primaryBank,
      date: prev.date || todayIso,
    }));
    setProjectSearch('');
    setProjectResults([]);
    setProjectInvoices([]);
    setUploadedFileName('');
    setTdsOption('none');
    setTdsAutoFill({ amount: null, bankTxn: null });
  };

  const tdsPct = calcTdsPercent();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-txn-dialog">
        <DialogHeader>
          <DialogTitle>{editId ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
          <DialogDescription>{editId ? 'Update this transaction' : 'Record a new credit or debit transaction'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {/* Type Toggle (hidden in edit mode to avoid wiping the form) */}
          {!editId && (
            <div className="flex gap-2">
              <button type="button" onClick={() => switchType('credit')}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${form.type === 'credit' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
                data-testid="txn-type-credit"><ArrowDownLeft className="h-4 w-4" />Credit (Payment In)</button>
              <button type="button" onClick={() => switchType('debit')}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${form.type === 'debit' ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
                data-testid="txn-type-debit"><ArrowUpRight className="h-4 w-4" />Debit (Expense)</button>
            </div>
          )}

          {/* ===== CREDIT FORM ===== */}
          {form.type === 'credit' && (
            <div className="space-y-3">
              <div className="space-y-1.5 relative" ref={projectRef}>
                <Label className="text-sm">Project</Label>
                <Input
                  value={projectSearch}
                  onChange={e => { setProjectSearch(e.target.value); setForm(f => ({...f, project_id: '', project_name: '', invoice_id: '', invoice_number: ''})); setProjectInvoices([]); }}
                  placeholder="Optional — leave blank for non-invoice income (e.g. Adsense, interest)"
                  data-testid="txn-project-search"
                />
                {showProjectDropdown && projectResults.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-[200px] overflow-y-auto" data-testid="project-dropdown">
                    {projectResults.map(p => (
                      <button key={p.id} type="button" onClick={() => handleSelectProject(p)}
                        className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors text-sm border-b border-border/30 last:border-0"
                        data-testid={`project-option-${p.id}`}>
                        <p className="font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.client_name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {form.project_id && (
                <div className="space-y-2">
                  <Label>Invoice <span className="text-red-400">*</span></Label>
                  {loadingInvoices ? <div className="text-sm text-muted-foreground">Loading invoices...</div> : (
                    projectInvoices.length === 0 ? (
                      <div className="text-sm text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded-md p-3 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <div>No invoices found for this project. Raise an invoice first, or clear the project to record a non-invoice transaction.</div>
                      </div>
                    ) : (
                      <div className="border border-border rounded-lg max-h-[180px] overflow-y-auto">
                        {projectInvoices.map(inv => {
                          // Exclude the current transaction being edited so
                          // the warning doesn't fire on its own row.
                          const otherPayments = (inv.existing_payments || []).filter(p => p.id !== editId);
                          const otherCount = otherPayments.length;
                          return (
                          <button key={inv.id} type="button" onClick={() => handleSelectInvoice(inv)}
                            className={`w-full text-left px-3 py-2 flex items-center justify-between transition-colors text-sm border-b border-border/30 last:border-0 ${form.invoice_id === inv.id ? 'bg-primary/15 ring-2 ring-primary/40' : 'hover:bg-muted/30'}`}
                            data-testid={`invoice-option-${inv.id}`}>
                            <div className="flex items-center gap-2">
                              {form.invoice_id === inv.id && <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />}
                              <Badge className={inv.invoice_type === 'proforma' ? 'bg-amber-500/10 text-amber-500 text-[10px] font-mono' : inv.invoice_type === 'tax' ? 'bg-blue-500/10 text-blue-500 text-[10px] font-mono' : 'bg-gray-500/10 text-gray-400 text-[10px]'}>
                                {inv.invoice_type === 'proforma' ? 'PI' : inv.invoice_type === 'tax' ? 'TAX' : 'Legacy'}
                              </Badge>
                              <span className="font-medium">{inv.invoice_number}</span>
                              <span className="text-muted-foreground">{formatDate(inv.invoice_date || inv.date)}</span>
                              {otherCount > 0 && (
                                <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[10px] gap-1 px-1.5 py-0" data-testid={`invoice-paid-flag-${inv.id}`}>
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  {otherCount} txn{otherCount > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={inv.status === 'paid' ? 'bg-emerald-500/10 text-emerald-500' : inv.status === 'draft' ? 'bg-gray-500/10 text-gray-400' : 'bg-amber-500/10 text-amber-500'} variant="outline">{inv.status}</Badge>
                              <span className="font-semibold">{formatCurrency(inv.total)}</span>
                            </div>
                          </button>
                        );})}
                      </div>
                    )
                  )}
                  {!form.invoice_id && projectInvoices.length > 0 && (
                    <p className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" />Select an invoice to proceed — TDS will be calculated on the invoice's pre-tax amount.</p>
                  )}
                </div>
              )}

              {form.invoice_id && form.invoice_number && (
                <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 flex items-center gap-2" data-testid="selected-invoice-banner">
                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Linked</p>
                    <p className="text-sm font-semibold truncate">{form.invoice_number}</p>
                    {form.invoice_subtotal > 0 && <span className="text-xs text-muted-foreground">Pre-tax: {formatCurrency(form.invoice_subtotal)}</span>}
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setForm(f => ({...f, invoice_id: '', invoice_number: '', invoice_subtotal: 0, invoice_total: 0, invoice_type: '', invoice_status: ''}))} data-testid="clear-invoice-btn">
                    Change
                  </Button>
                </div>
              )}

              {/* Soft duplicate-payment warning — non-blocking. Fires when
                  the selected invoice already has at least one credit
                  recorded against it (excluding the txn being edited). */}
              {form.invoice_id && form.type === 'credit' && (() => {
                const selectedInv = projectInvoices.find(i => i.id === form.invoice_id);
                if (!selectedInv) return null;
                const others = (selectedInv.existing_payments || []).filter(p => p.id !== editId);
                if (others.length === 0) return null;
                const totalPaid = others.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                return (
                  <div
                    className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-amber-500 text-xs"
                    data-testid="duplicate-payment-warning"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold mb-1">
                          {others.length} payment{others.length > 1 ? 's' : ''} already recorded against this invoice — total {formatCurrency(totalPaid)}
                        </div>
                        <ul className="space-y-0.5 text-[11px] text-amber-400/90">
                          {others.slice(0, 3).map(p => (
                            <li key={p.id} className="truncate" data-testid={`existing-payment-${p.id}`}>
                              • {formatDate(p.date)} — {formatCurrency(p.amount)}
                              {p.reference ? ` — Ref: ${p.reference}` : ''}
                            </li>
                          ))}
                          {others.length > 3 && (
                            <li className="text-[11px] italic">…and {others.length - 3} more</li>
                          )}
                        </ul>
                        <p className="mt-1.5 text-[11px] text-amber-400/80">
                          Verify you're not entering the same payment twice. You can still proceed if this is intentional (e.g. partial payments).
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Mark Invoice as Paid / Unpaid (Tax invoice or Proforma in create mode) */}
              {!editId && form.invoice_id && (form.invoice_type === 'tax' || form.invoice_type === 'proforma') && (
                <div className="space-y-1.5" data-testid="invoice-status-selector">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm">Tax Invoice Status</Label>
                    <InfoHint text={form.invoice_type === 'proforma'
                      ? 'Choosing "Mark as Paid" auto-generates the tax invoice as paid and triggers the tax-invoice email. "Keep as Unpaid" generates the tax invoice but leaves it open for further payments.'
                      : 'Choosing "Mark as Paid" closes this invoice and triggers the tax-invoice email if not already sent. "Keep as Unpaid" leaves it open for further payments.'} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label
                      className={`flex items-center justify-center gap-2 py-1.5 rounded-md border cursor-pointer transition-colors ${markPaidChoice === 'unpaid' ? 'border-amber-500 bg-amber-500/10 text-amber-500' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
                      data-testid="mark-unpaid-radio"
                    >
                      <input type="radio" name="mark_paid_choice" checked={markPaidChoice === 'unpaid'} onChange={() => setMarkPaidChoice('unpaid')} className="accent-amber-500 h-3.5 w-3.5" />
                      <span className="text-sm font-medium">Keep as Unpaid</span>
                    </label>
                    <label
                      className={`flex items-center justify-center gap-2 py-1.5 rounded-md border cursor-pointer transition-colors ${markPaidChoice === 'paid' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
                      data-testid="mark-paid-radio"
                    >
                      <input type="radio" name="mark_paid_choice" checked={markPaidChoice === 'paid'} onChange={() => setMarkPaidChoice('paid')} className="accent-emerald-500 h-3.5 w-3.5" />
                      <span className="text-sm font-medium">Mark as Paid</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm">Bank Account</Label>
                <Select value={form.bank_account} onValueChange={v => setForm(f => ({...f, bank_account: v}))}>
                  <SelectTrigger data-testid="txn-bank-account"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Amount (INR) *</Label>
                  <Input
                    type="number"
                    value={form.amount}
                    onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                    disabled={!!form.project_id && !form.invoice_id}
                    placeholder={form.project_id && !form.invoice_id ? 'Select an invoice first' : ''}
                    data-testid="txn-amount"
                  />
                </div>
                <div className="space-y-1.5"><Label className="text-sm">Date *</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} data-testid="txn-date" /></div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm">Bank Transaction Amount</Label>
                    <InfoHint text="The exact amount reflected in your bank statement — useful when multiple invoices are settled in one transfer." />
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground" data-testid="same-as-amount-checkbox">
                    <input type="checkbox" checked={form.bank_txn_amount !== '' && form.bank_txn_amount === form.amount} onChange={e => setForm(f => ({...f, bank_txn_amount: e.target.checked ? f.amount : ''}))} className="accent-primary h-3.5 w-3.5" />
                    Same as Amount
                  </label>
                </div>
                <Input type="number" value={form.bank_txn_amount} onChange={e => setForm(f => ({...f, bank_txn_amount: e.target.value}))} placeholder="e.g. 15000" data-testid="txn-bank-amount" />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm">TDS Deducted by Client</Label>
                  <InfoHint text="TDS % is calculated on the invoice base amount (excluding GST)." />
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {[{ label: 'No TDS', value: 'none' }, { label: '2%', value: '2' }, { label: '10%', value: '10' }].map(opt => (
                      <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm" data-testid={`tds-radio-${opt.value}`}>
                        <input type="radio" name="tds_option" checked={tdsOption === opt.value} onChange={() => handleTdsOptionChange(opt.value)} className="accent-primary h-3.5 w-3.5" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <Input type="number" value={form.tds_amount} onChange={e => { setForm(f => ({...f, tds_amount: e.target.value})); setTdsOption('none'); }} placeholder="TDS amount" className="flex-1" data-testid="txn-tds-amount" />
                  {tdsPct && (
                    <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/30 px-2 py-1 flex items-center gap-1 whitespace-nowrap text-[11px]" data-testid="tds-percentage">
                      <Percent className="h-3 w-3" />{tdsPct}%
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-sm">Payment Mode</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                    <SelectTrigger data-testid="txn-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{creditCategories.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-sm">Reference</Label><Input value={form.reference} onChange={e => setForm(f => ({...f, reference: e.target.value}))} placeholder="UTR / Cheque No." data-testid="txn-reference" /></div>
              </div>

              <div className="space-y-1.5"><Label className="text-sm">Description <span className="text-red-500">*</span></Label><Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Brief description of the transaction" required data-testid="txn-description" /></div>
            </div>
          )}

          {/* ===== DEBIT FORM ===== */}
          {form.type === 'debit' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Bank Account *</Label>
                <Select value={form.bank_account} onValueChange={v => setForm(f => ({...f, bank_account: v}))}>
                  <SelectTrigger data-testid="txn-debit-bank-account"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-sm">Amount (INR) *</Label><Input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} data-testid="txn-amount" /></div>
                <div className="space-y-1.5"><Label className="text-sm">Date *</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} data-testid="txn-date" /></div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm">TDS Deducted</Label>
                  <InfoHint text="TDS % is calculated on the base amount (excluding 18% GST)." />
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" value={form.tds_amount} onChange={e => setForm(f => ({...f, tds_amount: e.target.value}))} placeholder="TDS amount deducted" className="flex-1" data-testid="txn-tds-amount" />
                  {tdsPct && (
                    <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/30 px-2 py-1 flex items-center gap-1 whitespace-nowrap text-[11px]" data-testid="tds-percentage">
                      <Percent className="h-3 w-3" />{tdsPct}%
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-sm">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                    <SelectTrigger data-testid="txn-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{debitCategories.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-sm">Paid By</Label><Input value={form.paid_by} onChange={e => setForm(f => ({...f, paid_by: e.target.value}))} placeholder="Who made the payment" data-testid="txn-paid-by" /></div>
              </div>

              <div className="space-y-1.5"><Label className="text-sm">Description <span className="text-red-500">*</span></Label><Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Brief description of the transaction" required data-testid="txn-description" /></div>

              <div className="space-y-1.5">
                <Label className="text-sm">Invoice / Bill <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                {uploadedFileName ? (
                  <div className="flex items-center gap-2 p-2 border border-border rounded-lg bg-muted/20" data-testid="invoice-file-row">
                    <FileSpreadsheet className="h-4 w-4 text-primary shrink-0" />
                    {form.invoice_file ? (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/invoice-file/${form.invoice_file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm flex-1 truncate text-primary hover:underline underline-offset-2 cursor-pointer"
                        title="Click to open in a new tab"
                        data-testid="invoice-file-link"
                      >
                        {uploadedFileName}
                      </a>
                    ) : (
                      <span className="text-sm flex-1 truncate">{uploadedFileName}</span>
                    )}
                    {form.invoice_file && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transactions/invoice-file/${form.invoice_file}`, '_blank', 'noopener,noreferrer')}
                          title="Open in new tab"
                          data-testid="invoice-file-view"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/invoice-file/${form.invoice_file}`}
                          download={uploadedFileName || form.invoice_file_original_name || form.invoice_file}
                          className="h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                          title="Download"
                          data-testid="invoice-file-download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      </>
                    )}
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setForm(f => ({...f, invoice_file: '', invoice_file_original_name: ''})); setUploadedFileName(''); }} data-testid="remove-invoice-file"><X className="h-3 w-3" /></Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleInvoiceUpload} className="hidden" data-testid="invoice-file-input" />
                    <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors" data-testid="invoice-upload-zone">
                      {uploadingInvoice ? <Loader2 className="h-5 w-5 mx-auto animate-spin text-primary" /> : (
                        <>
                          <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">Click to upload invoice/bill</p>
                        </>
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-txn-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editId ? 'Update Transaction' : 'Create Transaction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionFormDialog;
